import React, { forwardRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { ASSET_CLASS_COLORS, getAssetClassColor } from '../../FactSheetApp/charts/AllocationDonutChart';

const DISPLAY_ORDER = [
  'Global Alpha',
  'Dynamic Plus Fund',
  'Australian Equities',
  'International Equities',
  'Real Estate',
  'Infrastructure',
  'Cash',
  'Government Bonds',
  'Corporate Debt',
  'Private Debt',
  'Private Equity',
  'Real Assets (Private Market)',
];

/** Merge "EM Equities" into "International Equities" */
const mergeEmEquities = (items) => {
  const merged = [];
  items.forEach(d => {
    const name = d.asset_class === 'EM Equities' ? 'International Equities' : d.asset_class;
    const existing = merged.findIndex(m => m.asset_class === name);
    if (existing !== -1) {
      merged[existing] = { ...merged[existing], weight: merged[existing].weight + d.weight };
    } else {
      merged.push({ ...d, asset_class: name });
    }
  });
  return merged;
};

const FC_PREFIX = 'FCEDGDRUA';

const AllocationDonutChart = forwardRef(({ allocation, colours = {}, portfolioCode = '' }, ref) => {
  if (!allocation || allocation.length === 0) {
    return <div style={{ color: '#999', fontStyle: 'italic', fontSize: '9pt' }}>No allocation data</div>;
  }

  const isFirstChoice = portfolioCode.startsWith(FC_PREFIX);

  const merged = mergeEmEquities(allocation).map(d => {
    if (isFirstChoice && d.asset_class === 'Global Alpha') {
      return { ...d, asset_class: 'Dynamic Plus Fund' };
    }
    return d;
  });

  const sorted = [...merged].sort((a, b) => {
    const idxA = DISPLAY_ORDER.indexOf(a.asset_class);
    const idxB = DISPLAY_ORDER.indexOf(b.asset_class);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });

  const colors = sorted.map((a, idx) => getAssetClassColor(a.asset_class, idx));

  const chartData = {
    labels: sorted.map(a => `${a.asset_class} ${a.weight.toFixed(1)}%`),
    datasets: [{
      data: sorted.map(a => a.weight),
      backgroundColor: colors,
      borderWidth: 1,
      borderColor: '#ffffff',
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 0 },
    cutout: '45%',
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        align: 'center',
        labels: {
          usePointStyle: true,
          pointStyle: 'rect',
          boxWidth: 8,
          boxHeight: 8,
          padding: 5,
          font: { size: 10 },
          color: '#333333',
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.raw.toFixed(1)}%`,
        },
      },
    },
  };

  return (
    <div style={{ width: '100%', height: '400px' }}>
      <Doughnut ref={ref} data={chartData} options={options} />
    </div>
  );
});

AllocationDonutChart.displayName = 'AllocationDonutChart';

export default AllocationDonutChart;
